// src/component/page/casino/CasinoPage.jsx
// HUESO Casino — frontend completo en un solo archivo.
// Incluye: Bone Flip, Bone Dice, Bone Mines, Harvest Wheel, Dog Race,
// pote comunitario, objetivo global, historial y tarjeta animada de entrada.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Slider,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";

/* ============================================================
   CONFIGURACIÓN AUTOCONTENIDA
============================================================ */

const URL_MAIN =
  process.env.NODE_ENV === "production"
    ? "https://bigdogbone.up.railway.app"
    : "http://localhost:3002";

const API = Object.freeze({
  status: `${URL_MAIN}/api/casino/status`,
  history: `${URL_MAIN}/api/casino/history`,
  communityStats: `${URL_MAIN}/api/casino/community-stats`,
  flip: `${URL_MAIN}/api/casino/bone-flip/play`,
  dice: `${URL_MAIN}/api/casino/bone-dice/play`,
  minesStart: `${URL_MAIN}/api/casino/bone-mines/start`,
  minesOpen: `${URL_MAIN}/api/casino/bone-mines/open`,
  minesCashout: `${URL_MAIN}/api/casino/bone-mines/cashout`,
  wheel: `${URL_MAIN}/api/casino/harvest-wheel/play`,
  race: `${URL_MAIN}/api/casino/dog-race/play`,
  claim: `${URL_MAIN}/api/casino/objective/claim`,
});

const FALLBACK_WHEEL_SEGMENTS = Object.freeze([
  { id: "DRY_LAND", label: "Dry Land", emoji: "🏜️", multiplier: 0 },
  { id: "SEED", label: "Seed", emoji: "🌱", multiplier: 0.5 },
  { id: "BONE", label: "Bone", emoji: "🦴", multiplier: 1 },
  { id: "RARE_PLANT", label: "Rare Plant", emoji: "🌿", multiplier: 1.5 },
  { id: "GOLDEN_DOG", label: "Golden Dog", emoji: "🐕", multiplier: 2 },
  { id: "CHEST", label: "Chest", emoji: "🎁", multiplier: 3 },
  { id: "JACKPOT", label: "Jackpot", emoji: "💎", multiplier: 5 },
]);

const FALLBACK_DOGS = Object.freeze([
  { id: "rocky", name: "Rocky", emoji: "🐕", chance: 30, multiplier: 3.166 },
  { id: "max", name: "Max", emoji: "🐶", chance: 25, multiplier: 3.8 },
  { id: "bruno", name: "Bruno", emoji: "🐕‍🦺", chance: 20, multiplier: 4.75 },
  { id: "luna", name: "Luna", emoji: "🐩", chance: 15, multiplier: 6.333 },
  { id: "zeus", name: "Zeus", emoji: "🐺", chance: 10, multiplier: 9.5 },
]);

/* ============================================================
   HELPERS
============================================================ */

function getCookie(name) {
  if (typeof document === "undefined") return "";
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

function getToken() {
  if (typeof window === "undefined") return "";
  return (
    getCookie("token") ||
    window.sessionStorage?.getItem("token") ||
    window.localStorage?.getItem("token") ||
    ""
  );
}

function authHeaders() {
  return { token: getToken() };
}

function requestId(prefix = "casino") {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function fmt(value, decimals = 3) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "0";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(number);
}

function clamp(value, min, max) {
  const number = Number(value);
  if (!Number.isFinite(number)) return min;
  return Math.max(min, Math.min(max, number));
}

function gameLabel(game) {
  const labels = {
    boneFlip: "Bone Flip",
    boneDice: "Bone Dice",
    boneMines: "Bone Mines",
    harvestWheel: "Harvest Wheel",
    dogRace: "Dog Race",
  };
  return labels[game] || game || "Casino";
}

function messageFromError(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "The casino could not complete this action."
  );
}

function syncGlobalBalance(balance) {
  const value = Number(balance);
  if (!Number.isFinite(value) || typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("actualizar-saldos", {
      detail: { bdb: { dineroReal: value } },
    }),
  );
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isWinningRound(round) {
  return ["WON", "CASHED_OUT"].includes(round?.status);
}

function roundDate(round) {
  const value = round?.settledAt || round?.createdAt;
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatSigned(value, decimals = 3) {
  const number = Number(value || 0);
  const sign = number > 0 ? "+" : "";
  return `${sign}${fmt(number, decimals)}`;
}

function netColor(value) {
  const number = Number(value || 0);
  if (number > 0) return "#73ffbd";
  if (number < 0) return "#ff7b91";
  return "rgba(255,255,255,.72)";
}

function rankMedal(index) {
  if (index === 0) return "🥇";
  if (index === 1) return "🥈";
  if (index === 2) return "🥉";
  return `#${index + 1}`;
}

function shortDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ============================================================
   IMAGEN INTERNA DEL CASINO
============================================================ */

const CASINO_HERO_SVG = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 620">
  <defs>
    <radialGradient id="bg" cx="50%" cy="35%" r="76%">
      <stop offset="0" stop-color="#7d2cff"/>
      <stop offset="0.42" stop-color="#24104c"/>
      <stop offset="1" stop-color="#05020d"/>
    </radialGradient>
    <linearGradient id="gold" x1="0" x2="1">
      <stop stop-color="#fff8b0"/>
      <stop offset="0.42" stop-color="#ffb000"/>
      <stop offset="1" stop-color="#ff6a00"/>
    </linearGradient>
    <linearGradient id="pink" x1="0" x2="1">
      <stop stop-color="#ff75df"/>
      <stop offset="1" stop-color="#6be8ff"/>
    </linearGradient>
    <filter id="glow">
      <feGaussianBlur stdDeviation="12" result="b"/>
      <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>
  </defs>
  <rect width="1200" height="620" fill="url(#bg)"/>
  <g opacity=".8">
    <circle cx="110" cy="92" r="5" fill="#fff"/>
    <circle cx="1040" cy="110" r="4" fill="#fff"/>
    <circle cx="970" cy="510" r="5" fill="#ffcf33"/>
    <circle cx="235" cy="515" r="4" fill="#ff4fd8"/>
    <circle cx="1120" cy="330" r="3" fill="#74f7ff"/>
    <circle cx="75" cy="360" r="3" fill="#74f7ff"/>
  </g>
  <g filter="url(#glow)">
    <rect x="165" y="65" width="870" height="490" rx="74" fill="#090513" stroke="url(#gold)" stroke-width="18"/>
    <rect x="200" y="100" width="800" height="420" rx="52" fill="none" stroke="url(#pink)" stroke-width="5" opacity=".9"/>
    <text x="600" y="205" text-anchor="middle" fill="url(#gold)" font-family="Arial Black" font-size="88">HUESO CASINO</text>
    <text x="600" y="285" text-anchor="middle" fill="#ffffff" font-family="Arial" font-weight="700" font-size="36">5 NEON MINI GAMES</text>
    <text x="600" y="407" text-anchor="middle" font-size="105">🎰 🦴 🎲 💣 🐕</text>
    <text x="600" y="482" text-anchor="middle" fill="#73ffcf" font-family="Arial Black" font-size="39">PLAY • WIN • FILL THE POT</text>
  </g>
</svg>`)}`;

/* ============================================================
   ANIMACIONES
============================================================ */

const neonPulse = keyframes`
  0%, 100% { box-shadow: 0 0 12px rgba(255,183,0,.42), 0 0 30px rgba(126,55,255,.28); }
  50% { box-shadow: 0 0 30px rgba(255,183,0,.94), 0 0 64px rgba(126,55,255,.72); }
`;

const floatCard = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const shine = keyframes`
  0% { transform: translateX(-170%) skewX(-24deg); }
  58%, 100% { transform: translateX(270%) skewX(-24deg); }
`;

const slotBounce = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  35% { transform: translateY(-10px) rotate(-4deg); }
  70% { transform: translateY(3px) rotate(3deg); }
`;

const coinFlip = keyframes`
  0% { transform: rotateY(0deg) scale(1); }
  50% { transform: rotateY(900deg) scale(1.28); }
  100% { transform: rotateY(1800deg) scale(1); }
`;

const diceRoll = keyframes`
  0% { transform: rotate(0deg) scale(.78); }
  50% { transform: rotate(540deg) scale(1.28); }
  100% { transform: rotate(1080deg) scale(1); }
`;

const spinWheel = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(1800deg); }
`;

const raceRun = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(calc(100% - 54px)); }
`;

const tileReveal = keyframes`
  0% { transform: rotateY(0deg) scale(.86); }
  55% { transform: rotateY(100deg) scale(1.15); }
  100% { transform: rotateY(180deg) scale(1); }
`;

const backgroundMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulseDot = keyframes`
  0%, 100% { opacity: .35; transform: scale(.8); }
  50% { opacity: 1; transform: scale(1.2); }
`;

/* ============================================================
   TARJETA DE ENTRADA PARA INICIO / OVERLAY
============================================================ */

export function CasinoEntryCard({
  path = "/game/casino",
  pot = 0,
  compact = false,
  onOpen,
}) {
  const [livePot, setLivePot] = useState(Number(pot || 0));

  useEffect(() => {
    let active = true;

    const loadPot = async () => {
      try {
        const response = await axios.get(API.status, {
          headers: authHeaders(),
        });
        if (active) {
          setLivePot(Number(response.data?.casino?.communityPot || 0));
        }
      } catch {
        // La tarjeta sigue mostrando el acceso aunque el backend esté reiniciando.
      }
    };

    loadPot();
    const timer = window.setInterval(loadPot, 30000);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  const openCasino = () => {
    if (typeof onOpen === "function") {
      onOpen();
      return;
    }
    window.location.assign(path);
  };

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        position: "relative",
        overflow: "hidden",
        borderRadius: "26px",
        border: "2px solid rgba(255,190,40,.88)",
        background: "#090313",
        animation: `${neonPulse} 2s ease-in-out infinite, ${floatCard} 4s ease-in-out infinite`,
      }}
    >
      <CardActionArea onClick={openCasino} sx={{ position: "relative" }}>
        <Box
          component="img"
          src={CASINO_HERO_SVG}
          alt="HUESO Casino"
          sx={{
            display: "block",
            width: "100%",
            height: compact ? 190 : { xs: 220, md: 285 },
            objectFit: "cover",
            transition: "transform .4s ease",
            "&:hover": { transform: "scale(1.045)" },
          }}
        />

        <Chip
          label="NEW"
          sx={{
            position: "absolute",
            top: 14,
            left: 14,
            color: "#180800",
            fontWeight: 1000,
            background: "linear-gradient(90deg,#fff59d,#ff9800)",
            animation: `${slotBounce} 1.35s ease-in-out infinite`,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              top: "-30%",
              left: 0,
              width: "32%",
              height: "160%",
              background:
                "linear-gradient(90deg,transparent,rgba(255,255,255,.38),transparent)",
              animation: `${shine} 3s ease-in-out infinite`,
            },
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: 14,
            right: 14,
            bottom: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: "16px",
            background: "rgba(3,0,10,.8)",
            border: "1px solid rgba(255,205,75,.68)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 1000, lineHeight: 1 }}>
              🎰 ENTER HUESO CASINO
            </Typography>
            <Typography
              sx={{ color: "#8dffd8", fontSize: 12, fontWeight: 850 }}
            >
              5 games · Rewards in HUESO
            </Typography>
          </Box>
          <Chip
            label={`POT ${fmt(livePot)} 🦴`}
            sx={{
              color: "#fff",
              fontWeight: 950,
              background: "linear-gradient(90deg,#7b1fa2,#ff6f00)",
            }}
          />
        </Box>
      </CardActionArea>
    </Card>
  );
}

/* ============================================================
   COMPONENTES VISUALES REUTILIZABLES
============================================================ */

function GlassPanel({ children, sx = {} }) {
  return (
    <Paper
      elevation={0}
      sx={{
        color: "white",
        border: "1px solid rgba(255,255,255,.12)",
        background:
          "linear-gradient(145deg,rgba(255,255,255,.075),rgba(255,255,255,.025))",
        backdropFilter: "blur(12px)",
        borderRadius: "22px",
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

function StatCard({ icon, label, value, accent = "#ffc43d", helper = "" }) {
  return (
    <GlassPanel
      sx={{
        p: 1.5,
        minHeight: 88,
        borderColor: `${accent}55`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 90,
          height: 90,
          right: -24,
          top: -30,
          borderRadius: "50%",
          background: accent,
          opacity: 0.12,
          filter: "blur(8px)",
        }}
      />
      <Typography
        sx={{ fontSize: 12, color: "rgba(255,255,255,.66)", fontWeight: 850 }}
      >
        {icon} {label}
      </Typography>
      <Typography
        sx={{ fontSize: { xs: 20, md: 25 }, color: accent, fontWeight: 1000 }}
      >
        {value}
      </Typography>
      {helper ? (
        <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,.48)" }}>
          {helper}
        </Typography>
      ) : null}
    </GlassPanel>
  );
}

function GameCard({ game, onPlay }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        minHeight: 290,
        position: "relative",
        overflow: "hidden",
        borderRadius: "24px",
        color: "white",
        border: `1px solid ${game.color}88`,
        background: `radial-gradient(circle at top, ${game.color}38, transparent 47%), linear-gradient(160deg,#171021,#05030a)`,
        transition:
          "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
        "&:hover": {
          transform: "translateY(-7px) scale(1.015)",
          boxShadow: `0 20px 52px ${game.color}42`,
          borderColor: game.color,
        },
      }}
    >
      <CardActionArea
        onClick={() => onPlay(game.id)}
        disabled={!game.enabled}
        sx={{ height: "100%", p: 2.25, textAlign: "center" }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            "&::after": {
              content: '""',
              position: "absolute",
              top: "-30%",
              left: "-60%",
              width: "35%",
              height: "165%",
              background:
                "linear-gradient(90deg,transparent,rgba(255,255,255,.15),transparent)",
              animation: `${shine} ${3.2 + game.order * 0.25}s ease-in-out infinite`,
            },
          }}
        />

        <Typography
          sx={{
            fontSize: 70,
            lineHeight: 1,
            mb: 1.2,
            filter: `drop-shadow(0 0 16px ${game.color})`,
            animation: `${slotBounce} ${2 + game.order * 0.12}s ease-in-out infinite`,
          }}
        >
          {game.emoji}
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 1000, color: game.color }}>
          {game.name}
        </Typography>

        <Typography
          sx={{ color: "rgba(255,255,255,.66)", fontSize: 13, minHeight: 40 }}
        >
          {game.description}
        </Typography>

        <Stack
          direction="row"
          justifyContent="center"
          spacing={0.7}
          sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 0.7 }}
        >
          <Chip
            size="small"
            label={`Min ${fmt(game.minBet)} 🦴`}
            sx={{ color: "white", bgcolor: "rgba(255,255,255,.08)" }}
          />
          <Chip
            size="small"
            label={`Max ${fmt(game.maxBet)} 🦴`}
            sx={{ color: "white", bgcolor: "rgba(255,255,255,.08)" }}
          />
        </Stack>

        <Button
          fullWidth
          variant="contained"
          disabled={!game.enabled}
          sx={{
            mt: 2,
            py: 1.15,
            fontWeight: 1000,
            color: "#120900",
            background: `linear-gradient(90deg,${game.color},#ffffff)`,
            "&:hover": { filter: "brightness(1.15)" },
          }}
        >
          {game.enabled ? "PLAY NOW" : "MAINTENANCE"}
        </Button>
      </CardActionArea>
    </Card>
  );
}

function CasinoDialog({
  open,
  title,
  subtitle,
  onClose,
  children,
  color = "#ffc43d",
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          color: "white",
          overflow: "hidden",
          borderRadius: { xs: "18px", sm: "28px" },
          border: `2px solid ${color}`,
          background:
            "radial-gradient(circle at top,rgba(104,38,211,.34),transparent 40%),linear-gradient(180deg,#140c24,#050309)",
          boxShadow: `0 0 58px ${color}55`,
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: "96vh", sm: "92vh" },
        },
      }}
    >
      <DialogTitle sx={{ pr: 7, pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 1000, color }}>
          {title}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,.62)", fontSize: 13 }}>
          {subtitle}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 10, color: "white" }}
          aria-label="Close game"
        >
          ✕
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, pb: 3 }}>
        {children}
      </DialogContent>
    </Dialog>
  );
}

function BetInput({ value, onChange, min, max, disabled, balance }) {
  const safeMin = Number(min || 0);
  const safeMax = Number(max || safeMin);

  return (
    <Box>
      <TextField
        fullWidth
        type="number"
        label="Bet in HUESO"
        value={value}
        disabled={disabled}
        inputProps={{ min: safeMin, max: safeMax, step: 1 }}
        onChange={(event) => onChange(event.target.value)}
        helperText={`Allowed: ${fmt(safeMin)} – ${fmt(safeMax)} HUESO · Balance: ${fmt(balance)}`}
        FormHelperTextProps={{ sx: { color: "rgba(255,255,255,.48)" } }}
        sx={{
          input: { color: "white", fontWeight: 900 },
          label: { color: "rgba(255,255,255,.65)" },
          "& .MuiOutlinedInput-root fieldset": {
            borderColor: "rgba(255,255,255,.28)",
          },
          "& .MuiOutlinedInput-root:hover fieldset": {
            borderColor: "rgba(255,211,78,.7)",
          },
        }}
      />

      <Stack direction="row" spacing={0.7} sx={{ mt: 1 }}>
        {[safeMin, 25, 50, 100, safeMax]
          .filter(
            (amount, index, array) =>
              amount >= safeMin &&
              amount <= safeMax &&
              array.indexOf(amount) === index,
          )
          .map((amount) => (
            <Button
              key={amount}
              size="small"
              variant="outlined"
              disabled={disabled}
              onClick={() => onChange(String(amount))}
              sx={{
                color: "#ffd65a",
                borderColor: "rgba(255,214,90,.45)",
                minWidth: 0,
                flex: 1,
                fontWeight: 900,
              }}
            >
              {fmt(amount)}
            </Button>
          ))}
      </Stack>
    </Box>
  );
}

function ResultBanner({ round }) {
  if (!round) return null;
  const won = isWinningRound(round);
  const open = round.status === "OPEN";

  return (
    <Alert
      severity={won ? "success" : open ? "info" : "error"}
      sx={{ mt: 2, fontWeight: 850, alignItems: "center" }}
    >
      <strong>{won ? "YOU WON" : open ? "ROUND OPEN" : "ROUND LOST"}</strong>
      {Number(round.payout || 0) > 0
        ? ` · ${fmt(round.payout)} HUESO · ${fmt(round.multiplier)}x`
        : ""}
    </Alert>
  );
}

function BusyButton({ busy, children, busyText, sx = {}, ...props }) {
  return (
    <Button
      {...props}
      disabled={busy || props.disabled}
      sx={{
        py: 1.35,
        fontWeight: 1000,
        position: "relative",
        ...sx,
      }}
    >
      {busy ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={17} sx={{ color: "inherit" }} />
          <span>{busyText}</span>
        </Stack>
      ) : (
        children
      )}
    </Button>
  );
}

function HistoryList({ rounds }) {
  if (!rounds.length) {
    return (
      <Typography
        sx={{ py: 3, textAlign: "center", color: "rgba(255,255,255,.5)" }}
      >
        Your casino history will appear here.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.8}>
      {rounds.map((round, index) => {
        const won = isWinningRound(round);
        return (
          <Box
            key={round.roundId || `${round.game}-${index}`}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr auto",
                sm: "1.25fr .7fr .7fr .8fr",
              },
              gap: 1,
              alignItems: "center",
              px: 1.25,
              py: 1,
              borderRadius: "13px",
              border: "1px solid rgba(255,255,255,.08)",
              bgcolor: won ? "rgba(58,255,169,.065)" : "rgba(255,70,100,.045)",
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: 13.5 }}>
                {gameLabel(round.game)}
              </Typography>
              <Typography
                sx={{ fontSize: 10.5, color: "rgba(255,255,255,.43)" }}
              >
                {roundDate(round)}
              </Typography>
            </Box>
            <Typography sx={{ textAlign: "right", fontSize: 12 }}>
              Bet <strong>{fmt(round.bet)}</strong>
            </Typography>
            <Typography
              sx={{
                display: { xs: "none", sm: "block" },
                textAlign: "right",
                fontSize: 12,
                color: won ? "#7fffc5" : "rgba(255,255,255,.55)",
              }}
            >
              {fmt(round.multiplier)}x
            </Typography>
            <Chip
              size="small"
              label={won ? `+${fmt(round.payout)} 🦴` : round.status}
              sx={{
                justifySelf: "end",
                color: won ? "#002416" : "white",
                fontWeight: 950,
                bgcolor: won ? "#73ffbd" : "rgba(255,90,116,.22)",
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
}

/* ============================================================
   ESTADÍSTICAS COMUNITARIAS
============================================================ */

function MiniLeaderboard({ title, icon, items = [], type = "net", emptyText }) {
  return (
    <GlassPanel
      sx={{
        p: 1.5,
        height: "100%",
        borderColor: "rgba(255,255,255,.12)",
        background:
          "linear-gradient(145deg,rgba(255,255,255,.065),rgba(255,255,255,.025))",
      }}
    >
      <Typography sx={{ mb: 1.1, fontWeight: 1000, color: "#ffd45d" }}>
        {icon} {title}
      </Typography>

      {!items.length ? (
        <Typography
          sx={{ py: 2, textAlign: "center", color: "rgba(255,255,255,.45)" }}
        >
          {emptyText || "No data yet."}
        </Typography>
      ) : (
        <Stack spacing={0.8}>
          {items.slice(0, 6).map((item, index) => {
            const value =
              type === "rounds"
                ? `${fmt(item.totalRounds, 0)} rounds`
                : type === "win"
                  ? `${formatSigned(item.playerNet)} HUESO`
                  : `${fmt(item.payout)} HUESO`;

            return (
              <Box
                key={`${title}-${item.nombre || item.roundId || index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "42px 1fr auto",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0.85,
                  borderRadius: "13px",
                  border: "1px solid rgba(255,255,255,.08)",
                  bgcolor:
                    index === 0
                      ? "rgba(255,215,75,.09)"
                      : "rgba(255,255,255,.035)",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 1000,
                    color: index < 3 ? "#ffd45d" : "rgba(255,255,255,.55)",
                  }}
                >
                  {rankMedal(index)}
                </Typography>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 950,
                      fontSize: 13,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.nombre || "unknown"}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: "rgba(255,255,255,.45)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {type === "biggest"
                      ? `${gameLabel(item.game)} · ${fmt(item.multiplier)}x`
                      : `${fmt(item.wins || 0, 0)}W / ${fmt(item.losses || 0, 0)}L · ${fmt(
                          item.winRate || 0,
                          1,
                        )}%`}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    justifySelf: "end",
                    fontSize: 12,
                    fontWeight: 1000,
                    color:
                      type === "rounds"
                        ? "#c18aff"
                        : type === "biggest"
                          ? "#73ffbd"
                          : netColor(item.playerNet),
                  }}
                >
                  {value}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </GlassPanel>
  );
}

function CommunityStatsBoard({
  stats,
  loading,
  range,
  game,
  onRangeChange,
  onGameChange,
  onRefresh,
}) {
  const summary = stats?.summary || {};
  const leaderboard = stats?.leaderboard || [];
  const gamesBreakdown = stats?.gamesBreakdown || [];
  const ranges = [
    { id: "today", label: "TODAY" },
    { id: "7d", label: "7 DAYS" },
    { id: "30d", label: "30 DAYS" },
    { id: "all", label: "ALL TIME" },
  ];
  const games = [
    { id: "all", label: "ALL GAMES" },
    { id: "boneFlip", label: "FLIP" },
    { id: "boneDice", label: "DICE" },
    { id: "boneMines", label: "MINES" },
    { id: "harvestWheel", label: "WHEEL" },
    { id: "dogRace", label: "RACE" },
  ];

  return (
    <GlassPanel
      sx={{
        mt: 2.5,
        p: { xs: 1.5, md: 2 },
        borderColor: "rgba(255,212,93,.35)",
        background:
          "radial-gradient(circle at top left,rgba(255,196,61,.14),transparent 35%),radial-gradient(circle at top right,rgba(207,125,255,.18),transparent 36%),linear-gradient(145deg,rgba(18,10,30,.92),rgba(4,3,9,.94))",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: 22, md: 28 },
              fontWeight: 1000,
              color: "#ffd45d",
              lineHeight: 1,
            }}
          >
            🏆 CASINO COMMUNITY BOARD
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,.58)", fontSize: 13, mt: 0.6 }}>
            Live player activity, wins, losses, payouts and community transparency.
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            onClick={onRefresh}
            disabled={loading}
            sx={{ color: "#ffd45d", fontWeight: 1000 }}
          >
            {loading ? "LOADING…" : "REFRESH"}
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {ranges.map((item) => (
          <Button
            key={item.id}
            size="small"
            variant={range === item.id ? "contained" : "outlined"}
            disabled={loading}
            onClick={() => onRangeChange(item.id)}
            sx={{
              fontWeight: 1000,
              color: range === item.id ? "#160900" : "#ffe486",
              borderColor: "rgba(255,212,93,.42)",
              background:
                range === item.id
                  ? "linear-gradient(90deg,#ffe66b,#ff9800)"
                  : "transparent",
            }}
          >
            {item.label}
          </Button>
        ))}
      </Stack>

      <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {games.map((item) => (
          <Button
            key={item.id}
            size="small"
            variant={game === item.id ? "contained" : "outlined"}
            disabled={loading}
            onClick={() => onGameChange(item.id)}
            sx={{
              fontWeight: 950,
              fontSize: 11,
              color: game === item.id ? "#00170c" : "#7bffd0",
              borderColor: "rgba(115,255,189,.35)",
              background:
                game === item.id
                  ? "linear-gradient(90deg,#73ffbd,#dcff70)"
                  : "transparent",
            }}
          >
            {item.label}
          </Button>
        ))}
      </Stack>

      <Grid container spacing={1.2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}>
          <StatCard
            icon="👥"
            label="PLAYERS"
            value={fmt(summary.activePlayers, 0)}
            accent="#73ffbd"
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            icon="🎮"
            label="ROUNDS"
            value={fmt(summary.totalRounds, 0)}
            accent="#c18aff"
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            icon="✅"
            label="WINS"
            value={fmt(summary.wins, 0)}
            accent="#62ffae"
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            icon="❌"
            label="LOSSES"
            value={fmt(summary.losses, 0)}
            accent="#ff7b91"
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            icon="💸"
            label="TOTAL BET"
            value={fmt(summary.totalBets)}
            accent="#ffd34e"
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            icon="🏦"
            label="CASINO NET"
            value={formatSigned(summary.casinoNet)}
            accent={netColor(summary.casinoNet)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={1.4}>
        <Grid item xs={12} md={4}>
          <MiniLeaderboard
            title="Top Winners"
            icon="🏆"
            items={stats?.topWinners || []}
            type="win"
            emptyText="No positive winners in this filter yet."
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <MiniLeaderboard
            title="Most Active"
            icon="🎮"
            items={stats?.mostActive || []}
            type="rounds"
            emptyText="No active players in this filter yet."
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <MiniLeaderboard
            title="Biggest Wins"
            icon="🔥"
            items={stats?.biggestWins || []}
            type="biggest"
            emptyText="No winning rounds in this filter yet."
          />
        </Grid>
      </Grid>

      <Grid container spacing={1.4} sx={{ mt: 0.2 }}>
        <Grid item xs={12} lg={8}>
          <GlassPanel sx={{ p: 1.5 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 1000, color: "#79e8ff" }}>
                  📊 FULL PLAYER STATS
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,.48)", fontSize: 11 }}>
                  Net result is shown from the player perspective.
                </Typography>
              </Box>
              <Chip
                size="small"
                label={`${range.toUpperCase()} · ${game === "all" ? "ALL" : gameLabel(game)}`}
                sx={{ color: "#dff8ff", bgcolor: "rgba(121,232,255,.12)" }}
              />
            </Stack>

            {!leaderboard.length ? (
              <Typography sx={{ py: 3, textAlign: "center", color: "rgba(255,255,255,.45)" }}>
                No player statistics found for this filter.
              </Typography>
            ) : (
              <Stack spacing={0.65}>
                {leaderboard.slice(0, 12).map((player, index) => (
                  <Box
                    key={`${player.nombre}-${index}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "38px 1fr auto",
                        md: "44px 1.1fr .7fr .7fr .9fr .9fr",
                      },
                      gap: 1,
                      alignItems: "center",
                      px: 1,
                      py: 0.9,
                      borderRadius: "13px",
                      bgcolor: "rgba(255,255,255,.035)",
                      border: "1px solid rgba(255,255,255,.07)",
                    }}
                  >
                    <Typography sx={{ fontWeight: 1000, color: "#ffd45d" }}>
                      {rankMedal(index)}
                    </Typography>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 950,
                          fontSize: 13,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {player.nombre}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,.45)" }}>
                        Favorite: {gameLabel(player.favoriteGame)} · Last:{" "}
                        {shortDate(player.lastPlayedAt)}
                      </Typography>
                    </Box>

                    <Typography sx={{ fontSize: 12, textAlign: "right" }}>
                      {fmt(player.totalRounds, 0)} rounds
                    </Typography>

                    <Typography
                      sx={{
                        display: { xs: "none", md: "block" },
                        fontSize: 12,
                        textAlign: "right",
                      }}
                    >
                      {fmt(player.wins, 0)}W / {fmt(player.losses, 0)}L
                    </Typography>

                    <Typography
                      sx={{
                        display: { xs: "none", md: "block" },
                        fontSize: 12,
                        textAlign: "right",
                        color: "rgba(255,255,255,.7)",
                      }}
                    >
                      Bet {fmt(player.totalBet)}
                    </Typography>

                    <Typography
                      sx={{
                        justifySelf: "end",
                        fontWeight: 1000,
                        fontSize: 12,
                        color: netColor(player.playerNet),
                      }}
                    >
                      {formatSigned(player.playerNet)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </GlassPanel>
        </Grid>

        <Grid item xs={12} lg={4}>
          <GlassPanel
            sx={{
              p: 1.5,
              height: "100%",
              borderColor: "rgba(115,255,189,.24)",
            }}
          >
            <Typography sx={{ fontWeight: 1000, color: "#73ffbd", mb: 1 }}>
              🕹️ GAME BREAKDOWN
            </Typography>

            {!gamesBreakdown.length ? (
              <Typography sx={{ py: 3, textAlign: "center", color: "rgba(255,255,255,.45)" }}>
                No game activity in this filter.
              </Typography>
            ) : (
              <Stack spacing={0.7}>
                {gamesBreakdown.map((item) => (
                  <Box
                    key={item.game}
                    sx={{
                      p: 1,
                      borderRadius: "13px",
                      bgcolor: "rgba(255,255,255,.035)",
                      border: "1px solid rgba(255,255,255,.07)",
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontWeight: 950, fontSize: 13 }}>
                        {gameLabel(item.game)}
                      </Typography>
                      <Typography sx={{ color: "#ffd45d", fontWeight: 950, fontSize: 12 }}>
                        {fmt(item.rounds, 0)} rounds
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={clamp(item.winRate || 0, 0, 100)}
                      sx={{
                        my: 0.75,
                        height: 8,
                        borderRadius: 10,
                        bgcolor: "rgba(255,255,255,.08)",
                        "& .MuiLinearProgress-bar": {
                          background: "linear-gradient(90deg,#73ffbd,#ffd45d)",
                        },
                      }}
                    />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}>
                        Win rate {fmt(item.winRate, 1)}%
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: netColor(item.casinoNet), fontWeight: 950 }}>
                        Casino {formatSigned(item.casinoNet)}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </GlassPanel>
        </Grid>
      </Grid>
    </GlassPanel>
  );
}

/* ============================================================
   CENTRO DE AYUDA DEL CASINO
============================================================ */

function HelpSection({ icon, title, accent = "#ffd34e", children }) {
  return (
    <GlassPanel
      sx={{
        p: { xs: 1.5, sm: 2 },
        height: "100%",
        borderColor: `${accent}55`,
        background: `linear-gradient(145deg,${accent}14,rgba(5,3,10,.9))`,
      }}
    >
      <Typography
        sx={{
          mb: 1,
          color: accent,
          fontSize: 16,
          fontWeight: 1000,
        }}
      >
        {icon} {title}
      </Typography>
      <Box
        sx={{
          color: "rgba(255,255,255,.72)",
          fontSize: 13,
          lineHeight: 1.7,
        }}
      >
        {children}
      </Box>
    </GlassPanel>
  );
}

function HelpLine({ children }) {
  return (
    <Typography
      sx={{
        position: "relative",
        pl: 2,
        mb: 0.65,
        color: "rgba(255,255,255,.72)",
        fontSize: 13,
        lineHeight: 1.65,
        "&::before": {
          content: '"•"',
          position: "absolute",
          left: 0,
          color: "#ffd45d",
          fontWeight: 1000,
        },
      }}
    >
      {children}
    </Typography>
  );
}

function CasinoHelpDialog({ open, onClose, games, casino }) {
  const getGame = (id) => games.find((game) => game.id === id) || {};

  const flip = getGame("boneFlip");
  const dice = getGame("boneDice");
  const mines = getGame("boneMines");
  const wheel = getGame("harvestWheel");
  const race = getGame("dogRace");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          color: "white",
          overflow: "hidden",
          borderRadius: { xs: "18px", sm: "28px" },
          border: "2px solid rgba(255,200,70,.9)",
          background:
            "radial-gradient(circle at top left,rgba(126,50,255,.35),transparent 35%),radial-gradient(circle at top right,rgba(255,105,180,.2),transparent 32%),linear-gradient(180deg,#140b25,#050309)",
          boxShadow:
            "0 0 35px rgba(255,190,40,.38),0 0 75px rgba(111,53,255,.3)",
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: "97vh", sm: "94vh" },
        },
      }}
    >
      <DialogTitle
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          pr: 8,
          borderBottom: "1px solid rgba(255,255,255,.1)",
          background:
            "linear-gradient(90deg,rgba(20,10,38,.98),rgba(45,12,58,.98))",
          backdropFilter: "blur(14px)",
        }}
      >
        <Typography
          sx={{
            color: "#ffd45d",
            fontSize: { xs: 22, sm: 28 },
            fontWeight: 1000,
          }}
        >
          ❓ HUESO CASINO HELP CENTER
        </Typography>
        <Typography
          sx={{
            mt: 0.4,
            color: "rgba(255,255,255,.62)",
            fontSize: 13,
          }}
        >
          Everything you need to understand balances, bets, games, rewards and
          casino protection.
        </Typography>

        <IconButton
          onClick={onClose}
          aria-label="Close help"
          sx={{
            position: "absolute",
            right: 14,
            top: 14,
            color: "white",
            bgcolor: "rgba(255,255,255,.07)",
            "&:hover": { bgcolor: "rgba(255,255,255,.14)" },
          }}
        >
          ✕
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: { xs: 1.3, sm: 2.5 },
          py: 2,
          borderColor: "rgba(255,255,255,.08)",
        }}
      >
        <Alert
          severity="info"
          sx={{
            mb: 2,
            color: "#dff8ff",
            bgcolor: "rgba(55,180,255,.12)",
            border: "1px solid rgba(90,210,255,.25)",
            "& .MuiAlert-icon": { color: "#6fe5ff" },
          }}
        >
          HUESO Casino uses your real in-game HUESO balance. Every bet can win
          or lose. Multipliers shown in each game include the returned stake.
          Never bet more than you are comfortable losing.
        </Alert>

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <HelpSection icon="🚀" title="QUICK START" accent="#73ffbd">
              <HelpLine>Select one of the five game cards and press PLAY NOW.</HelpLine>
              <HelpLine>
                Enter a bet inside the minimum and maximum limits shown on the
                game.
              </HelpLine>
              <HelpLine>
                Choose the required option, then confirm the play button.
              </HelpLine>
              <HelpLine>
                Your balance and casino statistics refresh after every completed
                action.
              </HelpLine>
              <HelpLine>
                A rejected action does not intentionally charge a new bet. Read
                the red or yellow message for the exact reason.
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="🦴" title="YOUR BALANCE" accent="#ffd34e">
              <HelpLine>
                YOUR HUESO is your spendable HUESO balance from the
                <strong> dineroReal</strong> field.
              </HelpLine>
              <HelpLine>
                Starting a valid round deducts the bet from this balance.
              </HelpLine>
              <HelpLine>
                A winning payout is credited after the backend settles the
                result.
              </HelpLine>
              <HelpLine>
                Example: a 10 HUESO bet paid at 1.8x returns 18 HUESO. The net
                profit is 8 HUESO.
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12}>
            <HelpSection icon="📊" title="DASHBOARD VALUES" accent="#63ccff">
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} lg={4}>
                  <HelpLine>
                    <strong>Community Pot:</strong> HUESO reserved for community
                    objective rewards.
                  </HelpLine>
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <HelpLine>
                    <strong>Casino Bank:</strong> currently available bankroll
                    used to cover valid payouts.
                  </HelpLine>
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <HelpLine>
                    <strong>Pending Burn:</strong> internal HUESO reserved for a
                    future token burn. It is not automatically burned on-chain.
                  </HelpLine>
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <HelpLine>
                    <strong>Operations:</strong> internal reserve assigned to
                    casino operating costs.
                  </HelpLine>
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <HelpLine>
                    <strong>Your Points:</strong> participation points earned
                    while a community objective is active.
                  </HelpLine>
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <HelpLine>
                    <strong>Rounds Played:</strong> total casino rounds recorded
                    by the backend.
                  </HelpLine>
                </Grid>
              </Grid>

              <Divider
                sx={{ my: 1.2, borderColor: "rgba(255,255,255,.08)" }}
              />

              <Typography
                sx={{
                  color: "rgba(255,255,255,.58)",
                  fontSize: 12,
                }}
              >
                Current public values: available bank {fmt(casino?.freeBankroll)}{" "}
                HUESO · community pot {fmt(casino?.communityPot)} HUESO · pending
                burn {fmt(casino?.burnReserve)} HUESO.
              </Typography>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="🪙" title="BONE FLIP" accent="#ffd34e">
              <HelpLine>Choose BONE or PAW before starting the round.</HelpLine>
              <HelpLine>
                The backend randomly selects one side. Matching the selected side
                wins.
              </HelpLine>
              <HelpLine>
                Current displayed winning payout:{" "}
                <strong>{fmt(flip.multiplier || 0)}x</strong>.
              </HelpLine>
              <HelpLine>
                Bet limits: {fmt(flip.minBet)} to {fmt(flip.maxBet)} HUESO.
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="🎲" title="BONE DICE" accent="#58d7ff">
              <HelpLine>
                UNDER wins when the roll is below your target. OVER wins when
                the roll is equal to or above your target.
              </HelpLine>
              <HelpLine>
                A lower win chance normally creates a larger payout multiplier.
              </HelpLine>
              <HelpLine>
                Allowed chance range: {fmt(dice.minChance, 0)}% to{" "}
                {fmt(dice.maxChance, 0)}%.
              </HelpLine>
              <HelpLine>
                The WIN CHANCE and EST. PAYOUT boxes update before you play.
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="💣" title="BONE MINES" accent="#ff596f">
              <HelpLine>
                Choose your bet and between {fmt(mines.minMines, 0)} and{" "}
                {fmt(mines.maxMines, 0)} mines.
              </HelpLine>
              <HelpLine>
                Open safe tiles to increase the current multiplier.
              </HelpLine>
              <HelpLine>
                After opening at least one safe tile, you can press CASH OUT NOW.
              </HelpLine>
              <HelpLine>
                Finding a mine ends the round and loses the active bet.
              </HelpLine>
              <HelpLine>
                Only one Mines round can stay open per player. Reloading the page
                restores an open round.
              </HelpLine>
              <HelpLine>
                Maximum displayed multiplier: {fmt(mines.maxMultiplier)}x.
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="🎡" title="HARVEST WHEEL" accent="#70ff9b">
              <HelpLine>
                Press SPIN THE WHEEL to receive the backend-selected segment.
              </HelpLine>
              <HelpLine>
                A 0x segment loses the bet, 0.5x returns half, 1x returns the
                stake, and values above 1x create a net win.
              </HelpLine>
              <Stack
                direction="row"
                spacing={0.7}
                useFlexGap
                flexWrap="wrap"
                sx={{ mt: 1 }}
              >
                {(wheel.segments || FALLBACK_WHEEL_SEGMENTS).map((segment) => (
                  <Chip
                    key={segment.id}
                    size="small"
                    label={`${segment.emoji} ${segment.label} ${fmt(
                      segment.multiplier,
                    )}x`}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(112,255,155,.1)",
                      border: "1px solid rgba(112,255,155,.22)",
                    }}
                  />
                ))}
              </Stack>
            </HelpSection>
          </Grid>

          <Grid item xs={12}>
            <HelpSection icon="🏁" title="DOG RACE" accent="#cf7dff">
              <HelpLine>
                Choose one dog before starting the race. You are paid only when
                your selected dog wins.
              </HelpLine>
              <HelpLine>
                Dogs with a higher chance normally pay a smaller multiplier.
                Dogs with a lower chance normally pay more.
              </HelpLine>

              <Stack
                direction="row"
                spacing={0.7}
                useFlexGap
                flexWrap="wrap"
                sx={{ mt: 1 }}
              >
                {(race.dogs || FALLBACK_DOGS).map((dog) => (
                  <Chip
                    key={dog.id}
                    size="small"
                    label={`${dog.emoji} ${dog.name} · ${fmt(
                      dog.chance,
                      0,
                    )}% · ${fmt(dog.multiplier)}x`}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(207,125,255,.1)",
                      border: "1px solid rgba(207,125,255,.24)",
                    }}
                  />
                ))}
              </Stack>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection
              icon="🎯"
              title="COMMUNITY OBJECTIVE"
              accent="#73ffbd"
            >
              <HelpLine>
                When the community pot reaches the backend minimum, a new global
                objective can begin.
              </HelpLine>
              <HelpLine>
                Bets made while the objective is active add betting volume and
                may grant participation points.
              </HelpLine>
              <HelpLine>
                Reward stages unlock as the global volume reaches 25%, 50%, 75%
                and 100%.
              </HelpLine>
              <HelpLine>
                Rewards are divided proportionally according to eligible points
                for each stage.
              </HelpLine>
              <HelpLine>
                Press CLAIM UNLOCKED REWARDS when you have an available reward.
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="🏦" title="BANK PROTECTION" accent="#ff9a5a">
              <HelpLine>
                The casino rejects a bet when the available bank cannot safely
                cover its maximum possible payout.
              </HelpLine>
              <HelpLine>
                If this happens, reduce the bet or wait until the bank has more
                available HUESO.
              </HelpLine>
              <HelpLine>
                Open Mines rounds reserve possible payout liability so the same
                HUESO is not promised to several rounds.
              </HelpLine>
              <HelpLine>
                The maximum bet shown in the game is a limit, not a guarantee
                that every maximum-size bet can always be accepted.
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="🔐" title="FAIRNESS & SECURITY" accent="#79e8ff">
              <HelpLine>
                Game results are calculated by the backend, not by the visual
                animation.
              </HelpLine>
              <HelpLine>
                Each request uses a unique request ID to reduce duplicate plays
                caused by repeated clicks.
              </HelpLine>
              <HelpLine>
                The casino stores round history, bets, payouts, multipliers and
                settlement status.
              </HelpLine>
              <HelpLine>
                Server-seed hashes and nonces support result verification and
                seed rotation.
              </HelpLine>
              <HelpLine>
                The frontend animation never decides whether a player wins or
                loses.
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="⚠️" title="IMPORTANT PLAY NOTES" accent="#ff7b91">
              <HelpLine>
                Every round is independent. A previous win does not guarantee a
                future loss, and a previous loss does not guarantee a future
                win.
              </HelpLine>
              <HelpLine>
                RTP is a long-term mathematical expectation, not a promise for a
                single player or session.
              </HelpLine>
              <HelpLine>
                Multiplier values include the returned stake unless the interface
                explicitly says otherwise.
              </HelpLine>
              <HelpLine>
                Use the recent-round history to review settled results.
              </HelpLine>
              <HelpLine>
                Stop playing whenever the activity is no longer enjoyable or the
                amount feels uncomfortable.
              </HelpLine>
            </HelpSection>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              minWidth: 210,
              py: 1.25,
              color: "#160900",
              fontWeight: 1000,
              background: "linear-gradient(90deg,#fff071,#ff9b00)",
              "&:hover": { filter: "brightness(1.12)" },
            }}
          >
            GOT IT — CLOSE HELP
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   PÁGINA PRINCIPAL
============================================================ */

export function CasinoPage() {
  const mounted = useRef(false);
  const actionLock = useRef(false);

  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [communityStats, setCommunityStats] = useState(null);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [communityRange, setCommunityRange] = useState("7d");
  const [communityGame, setCommunityGame] = useState("all");
  const [activeGame, setActiveGame] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [bet, setBet] = useState("10");
  const [result, setResult] = useState(null);
  const [notice, setNotice] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const [flipChoice, setFlipChoice] = useState("BONE");
  const [flipAnimating, setFlipAnimating] = useState(false);

  const [diceMode, setDiceMode] = useState("UNDER");
  const [diceTarget, setDiceTarget] = useState(50);
  const [diceAnimating, setDiceAnimating] = useState(false);

  const [mineCount, setMineCount] = useState(3);
  const [mineRound, setMineRound] = useState(null);
  const [lastMineCell, setLastMineCell] = useState(null);

  const [wheelSpinning, setWheelSpinning] = useState(false);

  const [dogId, setDogId] = useState("rocky");
  const [raceRunning, setRaceRunning] = useState(false);

  const notify = useCallback((text, type = "success") => {
    setNotice({ open: true, type, text });
  }, []);

  const loadStatus = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const response = await axios.get(API.status, {
          headers: authHeaders(),
        });
        if (!mounted.current) return;

        setStatus(response.data);
        setMineRound((current) => {
          const serverRound = response.data?.openMines || null;
          if (serverRound) return serverRound;
          if (current?.status === "OPEN") return current;
          return null;
        });
      } catch (error) {
        if (!silent) notify(messageFromError(error), "error");
      } finally {
        if (mounted.current && !silent) setLoading(false);
      }
    },
    [notify],
  );

  const loadHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API.history}?limit=14`, {
        headers: authHeaders(),
      });
      if (mounted.current) setHistory(response.data?.rounds || []);
    } catch {
      // El historial nunca debe bloquear la pantalla principal.
    }
  }, []);

  const loadCommunityStats = useCallback(
    async (silent = false) => {
      if (!silent) setCommunityLoading(true);

      try {
        const response = await axios.get(API.communityStats, {
          headers: authHeaders(),
          params: {
            range: communityRange,
            game: communityGame,
          },
        });

        if (mounted.current) {
          setCommunityStats(response.data || null);
        }
      } catch (error) {
        if (!silent) notify(messageFromError(error), "error");
      } finally {
        if (mounted.current && !silent) setCommunityLoading(false);
      }
    },
    [communityGame, communityRange, notify]
  );

  useEffect(() => {
    mounted.current = true;
    loadStatus();
    loadHistory();
    loadCommunityStats();

    const timer = window.setInterval(() => {
      loadStatus(true);
      loadCommunityStats(true);
    }, 15000);

    return () => {
      mounted.current = false;
      window.clearInterval(timer);
    };
  }, [loadCommunityStats, loadHistory, loadStatus]);

  const games = useMemo(() => {
    const source = status?.games || {};
    return [
      {
        id: "boneFlip",
        order: 1,
        name: "Bone Flip",
        emoji: "🪙",
        color: "#ffd34e",
        description: "Choose BONE or PAW and watch the neon coin fly.",
        enabled: true,
        minBet: 5,
        maxBet: 250,
        multiplier: 1.8,
        ...source.boneFlip,
      },
      {
        id: "boneDice",
        order: 2,
        name: "Bone Dice",
        emoji: "🎲",
        color: "#58d7ff",
        description: "Pick UNDER or OVER and control your own risk.",
        enabled: true,
        minBet: 5,
        maxBet: 250,
        minChance: 10,
        maxChance: 90,
        houseRtp: 0.8,
        ...source.boneDice,
      },
      {
        id: "boneMines",
        order: 3,
        name: "Bone Mines",
        emoji: "💣",
        color: "#ff596f",
        description: "Open safe bones, increase the multiplier and cash out.",
        enabled: true,
        minBet: 5,
        maxBet: 100,
        minMines: 1,
        maxMines: 10,
        maxMultiplier: 25,
        ...source.boneMines,
      },
      {
        id: "harvestWheel",
        order: 4,
        name: "Harvest Wheel",
        emoji: "🎡",
        color: "#70ff9b",
        description: "Spin the farm wheel for multipliers and a jackpot tile.",
        enabled: true,
        minBet: 5,
        maxBet: 250,
        segments: FALLBACK_WHEEL_SEGMENTS,
        ...source.harvestWheel,
      },
      {
        id: "dogRace",
        order: 5,
        name: "Dog Race",
        emoji: "🏁",
        color: "#cf7dff",
        description: "Back your favorite dog and watch the race explode.",
        enabled: true,
        minBet: 5,
        maxBet: 250,
        dogs: FALLBACK_DOGS,
        ...source.dogRace,
      },
    ];
  }, [status]);

  const selectedGame = games.find((game) => game.id === activeGame);
  const minBet = Number(selectedGame?.minBet || 5);
  const maxBet = Number(selectedGame?.maxBet || 250);
  const userBalance = Number(status?.user?.balance || 0);
  const casino = status?.casino || {};
  const objective = casino.objective || {};

  const validateBet = useCallback(() => {
    const amount = Number(bet);
    if (!Number.isFinite(amount)) {
      notify("Enter a valid HUESO bet.", "warning");
      return false;
    }
    if (amount < minBet || amount > maxBet) {
      notify(
        `The bet must be between ${fmt(minBet)} and ${fmt(maxBet)} HUESO.`,
        "warning",
      );
      return false;
    }
    if (amount > userBalance) {
      notify("You do not have enough HUESO for this bet.", "error");
      return false;
    }
    if (!casino.enabled) {
      notify(
        casino.pausedReason || "The casino is currently paused.",
        "warning",
      );
      return false;
    }
    return true;
  }, [
    bet,
    casino.enabled,
    casino.pausedReason,
    maxBet,
    minBet,
    notify,
    userBalance,
  ]);

  const refreshAfterAction = useCallback(async () => {
    await Promise.all([loadStatus(true), loadHistory(), loadCommunityStats(true)]);
  }, [loadCommunityStats, loadHistory, loadStatus]);

  const openGame = (gameId) => {
    const game = games.find((item) => item.id === gameId);
    if (!game?.enabled) {
      notify("This game is currently in maintenance.", "warning");
      return;
    }

    setResult(null);
    setLastMineCell(null);
    setBet(String(Math.max(Number(game.minBet || 5), 10)));
    setActiveGame(gameId);

    if (gameId === "dogRace") {
      setDogId(game.dogs?.[0]?.id || "rocky");
    }
    if (gameId === "boneMines" && mineRound?.status === "OPEN") {
      setResult(mineRound);
    }
  };

  const closeGame = () => {
    if (playing) return;
    setActiveGame("");
    setResult(null);
    setLastMineCell(null);
  };

  const postInstantGame = useCallback(
    async ({ url, body, animationMs = 900, before, after }) => {
      if (actionLock.current || !validateBet()) return null;

      actionLock.current = true;
      setPlaying(true);
      setResult(null);
      if (before) before();

      try {
        const response = await axios.post(
          url,
          {
            ...body,
            bet: Number(bet),
            requestId: requestId(activeGame || "casino"),
            clientSeed: `${status?.user?.nombre || "player"}-${Date.now()}`,
          },
          { headers: authHeaders() },
        );

        const data = response.data;
        syncGlobalBalance(data.balance);

        if (animationMs > 0) await sleep(animationMs);
        if (!mounted.current) return data;

        setResult(data.round || null);
        if (after) after(data);

        notify(
          Number(data.round?.payout || 0) > 0
            ? `You received ${fmt(data.round.payout)} HUESO!`
            : "Round completed.",
          Number(data.round?.payout || 0) > 0 ? "success" : "info",
        );

        await refreshAfterAction();
        return data;
      } catch (error) {
        notify(messageFromError(error), "error");
        return null;
      } finally {
        actionLock.current = false;
        if (mounted.current) setPlaying(false);
      }
    },
    [
      activeGame,
      bet,
      notify,
      refreshAfterAction,
      status?.user?.nombre,
      validateBet,
    ],
  );

  const playFlip = () =>
    postInstantGame({
      url: API.flip,
      body: { choice: flipChoice },
      animationMs: 1000,
      before: () => setFlipAnimating(true),
      after: () => setFlipAnimating(false),
    }).finally(() => setFlipAnimating(false));

  const playDice = () =>
    postInstantGame({
      url: API.dice,
      body: { mode: diceMode, target: Number(diceTarget) },
      animationMs: 950,
      before: () => setDiceAnimating(true),
      after: () => setDiceAnimating(false),
    }).finally(() => setDiceAnimating(false));

  const playWheel = () =>
    postInstantGame({
      url: API.wheel,
      body: {},
      animationMs: 1550,
      before: () => setWheelSpinning(true),
      after: () => setWheelSpinning(false),
    }).finally(() => setWheelSpinning(false));

  const playRace = () =>
    postInstantGame({
      url: API.race,
      body: { dogId },
      animationMs: 1250,
      before: () => setRaceRunning(true),
      after: () => setRaceRunning(false),
    }).finally(() => setRaceRunning(false));

  const startMineGame = async () => {
    if (actionLock.current || !validateBet()) return;

    const minMines = Number(selectedGame?.minMines || 1);
    const maxMines = Number(selectedGame?.maxMines || 10);
    if (mineCount < minMines || mineCount > maxMines) {
      notify(`Choose between ${minMines} and ${maxMines} mines.`, "warning");
      return;
    }

    actionLock.current = true;
    setPlaying(true);
    setLastMineCell(null);

    try {
      const response = await axios.post(
        API.minesStart,
        {
          bet: Number(bet),
          mineCount: Number(mineCount),
          requestId: requestId("mines"),
          clientSeed: `${status?.user?.nombre || "player"}-${Date.now()}`,
        },
        { headers: authHeaders() },
      );

      syncGlobalBalance(response.data?.balance);
      setMineRound(response.data?.round || null);
      setResult(response.data?.round || null);
      notify("Bone Mines round started. Open a tile!", "info");
      await loadStatus(true);
    } catch (error) {
      notify(messageFromError(error), "error");
    } finally {
      actionLock.current = false;
      if (mounted.current) setPlaying(false);
    }
  };

  const openMineCell = async (cell) => {
    if (
      !mineRound?.roundId ||
      mineRound.status !== "OPEN" ||
      actionLock.current ||
      mineRound.openedCells?.includes(cell)
    ) {
      return;
    }

    actionLock.current = true;
    setPlaying(true);
    setLastMineCell(cell);

    try {
      const response = await axios.post(
        API.minesOpen,
        { roundId: mineRound.roundId, cell },
        { headers: authHeaders() },
      );

      await sleep(420);
      const updated = response.data?.round;
      syncGlobalBalance(response.data?.balance);
      setMineRound(updated || null);
      setResult(updated || null);

      if (response.data?.mineHit) {
        notify("BOOM! You found a mine.", "error");
      } else if (response.data?.autoCashedOut) {
        notify(`Automatic cashout: ${fmt(updated?.payout)} HUESO`, "success");
      } else {
        notify(`Safe tile · ${fmt(updated?.currentMultiplier)}x`, "success");
      }

      await refreshAfterAction();
    } catch (error) {
      notify(messageFromError(error), "error");
    } finally {
      actionLock.current = false;
      if (mounted.current) setPlaying(false);
    }
  };

  const cashoutMines = async () => {
    if (
      !mineRound?.roundId ||
      mineRound.status !== "OPEN" ||
      actionLock.current
    )
      return;

    actionLock.current = true;
    setPlaying(true);

    try {
      const response = await axios.post(
        API.minesCashout,
        { roundId: mineRound.roundId },
        { headers: authHeaders() },
      );

      const updated = response.data?.round;
      syncGlobalBalance(response.data?.balance);
      setMineRound(updated || null);
      setResult(updated || null);
      notify(`Cashed out ${fmt(updated?.payout)} HUESO!`, "success");
      await refreshAfterAction();
    } catch (error) {
      notify(messageFromError(error), "error");
    } finally {
      actionLock.current = false;
      if (mounted.current) setPlaying(false);
    }
  };

  const claimRewards = async () => {
    if (actionLock.current) return;

    actionLock.current = true;
    setPlaying(true);

    try {
      const response = await axios.post(
        API.claim,
        {},
        { headers: authHeaders() },
      );
      const amount = Number(response.data?.claimed || 0);
      syncGlobalBalance(response.data?.balance);

      notify(
        amount > 0
          ? `Objective reward claimed: ${fmt(amount)} HUESO!`
          : "You do not have an objective reward available yet.",
        amount > 0 ? "success" : "info",
      );

      await refreshAfterAction();
    } catch (error) {
      notify(messageFromError(error), "error");
    } finally {
      actionLock.current = false;
      if (mounted.current) setPlaying(false);
    }
  };

  const diceChance =
    diceMode === "UNDER" ? Number(diceTarget) : 100 - Number(diceTarget);
  const diceRtp = Number(selectedGame?.houseRtp || 0.8);
  const diceEstimatedMultiplier =
    diceChance > 0 ? diceRtp / (diceChance / 100) : 0;
  const minePayoutPreview =
    Number(mineRound?.bet || bet || 0) *
    Number(mineRound?.currentMultiplier || 0);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#050309",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <Box
            component="img"
            src={CASINO_HERO_SVG}
            alt="HUESO Casino loading"
            sx={{ width: { xs: 260, sm: 410 }, borderRadius: 3, opacity: 0.92 }}
          />
          <CircularProgress sx={{ color: "#ffc43d" }} />
          <Typography sx={{ color: "white", fontWeight: 900 }}>
            Opening HUESO Casino…
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        color: "white",
        py: { xs: 2, md: 4 },
        background:
          "radial-gradient(circle at 15% 12%,rgba(255,90,200,.22),transparent 28%),radial-gradient(circle at 85% 15%,rgba(72,92,255,.25),transparent 32%),linear-gradient(120deg,#04020a,#13051f,#05020b)",
        backgroundSize: "200% 200%",
        animation: `${backgroundMove} 16s ease infinite`,
      }}
    >
      <Container maxWidth="xl">
        {/* HERO */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            p: { xs: 2, md: 3 },
            mb: 2,
            borderRadius: "28px",
            color: "white",
            border: "2px solid rgba(255,193,50,.72)",
            background:
              "linear-gradient(120deg,rgba(15,5,30,.94),rgba(55,10,75,.88),rgba(8,4,18,.94))",
            animation: `${neonPulse} 2.5s ease-in-out infinite`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.1,
              backgroundImage: `url("${CASINO_HERO_SVG}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />

          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 1000,
                  fontSize: { xs: 31, md: 51 },
                  lineHeight: 0.95,
                  background:
                    "linear-gradient(90deg,#fff7a0,#ffae00,#ff70d7,#79e9ff)",
                  backgroundClip: "text",
                  color: "transparent",
                  filter: "drop-shadow(0 0 16px rgba(255,180,0,.45))",
                }}
              >
                🎰 HUESO CASINO
              </Typography>
              <Typography
                sx={{ mt: 1, color: "rgba(255,255,255,.72)", fontWeight: 800 }}
              >
                Five provably-fair mini games · Powered by Big Dog Bone
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: casino.enabled ? "#62ffae" : "#ff715f",
                    boxShadow: casino.enabled
                      ? "0 0 12px #62ffae"
                      : "0 0 12px #ff715f",
                    animation: `${pulseDot} 1.3s ease-in-out infinite`,
                  }}
                />
                <Typography
                  sx={{ fontSize: 12, color: "rgba(255,255,255,.56)" }}
                >
                  {casino.enabled ? "CASINO ONLINE" : "CASINO PAUSED"}
                </Typography>
              </Stack>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
            >
              <Button
                variant="outlined"
                onClick={() => setHelpOpen(true)}
                sx={{
                  color: "#fff4a6",
                  borderColor: "rgba(255,218,80,.72)",
                  fontWeight: 1000,
                  background: "rgba(255,190,40,.08)",
                  boxShadow: "0 0 15px rgba(255,190,40,.12)",
                  "&:hover": {
                    borderColor: "#ffd34e",
                    background: "rgba(255,190,40,.16)",
                    boxShadow: "0 0 22px rgba(255,190,40,.28)",
                  },
                }}
              >
                ❓ HELP
              </Button>

              <Button
                variant="outlined"
                onClick={() => window.history.back()}
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,.4)",
                  fontWeight: 900,
                }}
              >
                ← BACK
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  loadStatus();
                  loadHistory();
                  loadCommunityStats();
                }}
                sx={{
                  fontWeight: 1000,
                  color: "#170b00",
                  background: "linear-gradient(90deg,#ffde59,#ff8f00)",
                }}
              >
                ↻ REFRESH
              </Button>
            </Stack>
          </Box>
        </Paper>

        {!casino.enabled && (
          <Alert severity="warning" sx={{ mb: 2, fontWeight: 850 }}>
            {casino.pausedReason || "Casino is currently paused."}
          </Alert>
        )}

        {/* ESTADÍSTICAS Y ECONOMÍA DEL CASINO */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              lg: "repeat(7, minmax(0, 1fr))",
            },
            gap: 1.4,
            mb: 2,
          }}
        >
          <StatCard
            icon="🦴"
            label="YOUR HUESO"
            value={fmt(userBalance)}
            accent="#ffd34e"
          />

          <StatCard
            icon="🎁"
            label="COMMUNITY POT"
            value={fmt(casino.communityPot)}
            accent="#73ffbd"
            helper="Community rewards"
          />

          <StatCard
            icon="🏦"
            label="CASINO BANK"
            value={fmt(casino.freeBankroll)}
            accent="#63ccff"
            helper="Available bankroll"
          />

          <StatCard
            icon="🔥"
            label="PENDING BURN"
            value={fmt(casino.burnReserve)}
            accent="#ff7043"
            helper="Reserved for token burn"
          />

          <StatCard
            icon="⚙️"
            label="OPERATIONS"
            value={fmt(casino.operationsReserve)}
            accent="#b39ddb"
            helper="Operations reserve"
          />

          <StatCard
            icon="🎯"
            label="YOUR POINTS"
            value={fmt(status?.user?.totalPoints, 0)}
            accent="#ff78d2"
          />

          <StatCard
            icon="🎮"
            label="ROUNDS PLAYED"
            value={fmt(casino.stats?.totalRounds, 0)}
            accent="#c18aff"
          />
        </Box>

        {/* OBJETIVO GLOBAL */}
        <GlassPanel
          sx={{
            p: 2,
            mb: 2,
            borderColor: "rgba(114,255,194,.35)",
            background:
              "linear-gradient(135deg,rgba(29,105,78,.22),rgba(8,8,18,.82))",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1.5,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Typography sx={{ fontWeight: 1000, color: "#7bffd0" }}>
                🎯 GLOBAL OBJECTIVE · Stage{" "}
                {Math.min(4, Number(objective.currentStage || 0) + 1)}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,.62)", fontSize: 13 }}>
                Community betting volume unlocks HUESO rewards for participating
                players.
              </Typography>

              <LinearProgress
                variant="determinate"
                value={clamp(Number(objective.progressPercent || 0), 0, 100)}
                sx={{
                  mt: 1.2,
                  height: 13,
                  borderRadius: 10,
                  bgcolor: "rgba(255,255,255,.09)",
                  "& .MuiLinearProgress-bar": {
                    background:
                      "linear-gradient(90deg,#3dffa6,#ffe75a,#ff6bcf)",
                  },
                }}
              />

              <Typography
                sx={{ mt: 0.6, fontSize: 12, color: "rgba(255,255,255,.7)" }}
              >
                {fmt(objective.progressVolume)} / {fmt(objective.targetVolume)}{" "}
                HUESO · {fmt(objective.progressPercent, 2)}%
              </Typography>
            </Box>

            <Stack alignItems={{ xs: "stretch", sm: "flex-end" }}>
              <Chip
                label={`REWARD ESCROW ${fmt(objective.escrowRemaining)} 🦴`}
                sx={{
                  color: "white",
                  fontWeight: 900,
                  bgcolor: "rgba(80,255,177,.16)",
                }}
              />
              <Button
                disabled={playing}
                onClick={claimRewards}
                variant="contained"
                sx={{
                  mt: 1,
                  fontWeight: 1000,
                  color: "#00170d",
                  background: "linear-gradient(90deg,#73ffbd,#d9ff6f)",
                }}
              >
                CLAIM UNLOCKED REWARDS
              </Button>
            </Stack>
          </Box>
        </GlassPanel>

        {/* JUEGOS */}
        <Grid container spacing={2}>
          {games.map((game) => (
            <Grid
              item
              xs={12}
              sm={6}
              lg={game.id === "dogRace" ? 12 : 3}
              key={game.id}
            >
              <GameCard game={game} onPlay={openGame} />
            </Grid>
          ))}
        </Grid>

        <CommunityStatsBoard
          stats={communityStats}
          loading={communityLoading}
          range={communityRange}
          game={communityGame}
          onRangeChange={setCommunityRange}
          onGameChange={setCommunityGame}
          onRefresh={() => loadCommunityStats()}
        />

        {/* HISTORIAL Y TRANSPARENCIA */}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} lg={8}>
            <GlassPanel sx={{ p: 2, height: "100%" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.2 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>
                    📜 YOUR RECENT ROUNDS
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12, color: "rgba(255,255,255,.5)" }}
                  >
                    Latest results registered by the backend.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={loadHistory}
                  sx={{ color: "#ffd45d", fontWeight: 900 }}
                >
                  REFRESH
                </Button>
              </Stack>
              <HistoryList rounds={history} />
            </GlassPanel>
          </Grid>

          <Grid item xs={12} lg={4}>
            <GlassPanel
              sx={{
                p: 2,
                height: "100%",
                borderColor: "rgba(120,212,255,.28)",
                background:
                  "linear-gradient(145deg,rgba(28,96,141,.18),rgba(7,5,15,.82))",
              }}
            >
              <Typography
                sx={{ fontWeight: 1000, fontSize: 18, color: "#79e8ff" }}
              >
                🔐 FAIR & PROTECTED
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  color: "rgba(255,255,255,.66)",
                  fontSize: 13,
                  lineHeight: 1.65,
                }}
              >
                Every result is calculated by the backend. The frontend only
                displays the animation. Bets and payouts are protected by
                backend settlement controls, request IDs, unique records and a
                public server-seed hash.
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: "rgba(255,255,255,.09)" }} />
              <Stack spacing={0.8}>
                <Typography sx={{ fontSize: 12 }}>
                  ✅ Double-click protection
                </Typography>
                <Typography sx={{ fontSize: 12 }}>
                  ✅ Bank payout limits
                </Typography>
                <Typography sx={{ fontSize: 12 }}>
                  ✅ Full round history
                </Typography>
                <Typography sx={{ fontSize: 12 }}>
                  ✅ Community objective rewards
                </Typography>
              </Stack>
              <Chip
                label={`SERVER HASH ${String(casino?.fairness?.serverSeedHash || "not available").slice(0, 16)}…`}
                sx={{
                  mt: 2,
                  maxWidth: "100%",
                  color: "#bfefff",
                  bgcolor: "rgba(73,190,255,.12)",
                }}
              />
            </GlassPanel>
          </Grid>
        </Grid>
      </Container>

      <CasinoHelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        games={games}
        casino={casino}
      />

      {/* ======================================================
          BONE FLIP
      ====================================================== */}
      <CasinoDialog
        open={activeGame === "boneFlip"}
        onClose={closeGame}
        title="🪙 Bone Flip"
        subtitle={`Choose BONE or PAW. Winning payout: ${fmt(selectedGame?.multiplier || 1.8)}x.`}
        color="#ffd34e"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <BetInput
              value={bet}
              onChange={setBet}
              min={minBet}
              max={maxBet}
              disabled={playing}
              balance={userBalance}
            />

            <Typography
              sx={{
                mt: 2,
                mb: 0.8,
                color: "rgba(255,255,255,.65)",
                fontSize: 12,
              }}
            >
              CHOOSE YOUR SIDE
            </Typography>

            <Stack direction="row" spacing={1}>
              {["BONE", "PAW"].map((choice) => (
                <Button
                  key={choice}
                  fullWidth
                  variant={flipChoice === choice ? "contained" : "outlined"}
                  disabled={playing}
                  onClick={() => setFlipChoice(choice)}
                  sx={{
                    py: 1.3,
                    color: flipChoice === choice ? "#1c1000" : "white",
                    borderColor: "rgba(255,211,78,.55)",
                    background:
                      flipChoice === choice
                        ? "linear-gradient(90deg,#fff17c,#ff9d00)"
                        : "transparent",
                    fontWeight: 1000,
                  }}
                >
                  {choice === "BONE" ? "🦴 BONE" : "🐾 PAW"}
                </Button>
              ))}
            </Stack>

            <BusyButton
              fullWidth
              variant="contained"
              onClick={playFlip}
              busy={playing}
              busyText="FLIPPING…"
              sx={{
                mt: 2,
                color: "#1d1000",
                background: "linear-gradient(90deg,#ffe95a,#ff8b00)",
              }}
            >
              FLIP THE COIN
            </BusyButton>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box sx={{ minHeight: 330, display: "grid", placeItems: "center" }}>
              <Box
                sx={{
                  width: 205,
                  height: 205,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  fontSize: 82,
                  border: "12px double #fff3a0",
                  color: "#fff",
                  background:
                    "radial-gradient(circle at 35% 30%,#fff7a5,#ffb000 35%,#a95100 78%,#281000)",
                  boxShadow: "0 0 55px rgba(255,190,50,.6)",
                  transformStyle: "preserve-3d",
                  animation: flipAnimating
                    ? `${coinFlip} 1s ease-in-out`
                    : "none",
                }}
              >
                {result?.result?.side === "PAW" ? "🐾" : "🦴"}
              </Box>
              <Typography
                sx={{
                  mt: 1.5,
                  color: "#ffe66a",
                  fontWeight: 1000,
                  fontSize: 22,
                }}
              >
                {result?.result?.side || "READY"}
              </Typography>
            </Box>
            <ResultBanner round={result} />
          </Grid>
        </Grid>
      </CasinoDialog>

      {/* ======================================================
          BONE DICE
      ====================================================== */}
      <CasinoDialog
        open={activeGame === "boneDice"}
        onClose={closeGame}
        title="🎲 Bone Dice"
        subtitle="Control the probability. Lower chance means a higher multiplier."
        color="#58d7ff"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <BetInput
              value={bet}
              onChange={setBet}
              min={minBet}
              max={maxBet}
              disabled={playing}
              balance={userBalance}
            />

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {["UNDER", "OVER"].map((mode) => (
                <Button
                  key={mode}
                  fullWidth
                  variant={diceMode === mode ? "contained" : "outlined"}
                  onClick={() => setDiceMode(mode)}
                  disabled={playing}
                  sx={{
                    color: diceMode === mode ? "#00151d" : "white",
                    borderColor: "rgba(88,215,255,.55)",
                    background:
                      diceMode === mode
                        ? "linear-gradient(90deg,#58d7ff,#b5f5ff)"
                        : "transparent",
                    fontWeight: 1000,
                  }}
                >
                  {mode}
                </Button>
              ))}
            </Stack>

            <Box sx={{ mt: 2, px: 1 }}>
              <Typography sx={{ fontWeight: 900, color: "#74e3ff" }}>
                Target: {Number(diceTarget).toFixed(0)}
              </Typography>
              <Slider
                value={Number(diceTarget)}
                min={10}
                max={90}
                step={1}
                disabled={playing}
                onChange={(_, value) => setDiceTarget(Number(value))}
                sx={{ color: "#58d7ff" }}
              />
            </Box>

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <GlassPanel sx={{ p: 1.2, textAlign: "center" }}>
                  <Typography
                    sx={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}
                  >
                    WIN CHANCE
                  </Typography>
                  <Typography
                    sx={{ color: "#63ddff", fontWeight: 1000, fontSize: 22 }}
                  >
                    {fmt(diceChance, 2)}%
                  </Typography>
                </GlassPanel>
              </Grid>
              <Grid item xs={6}>
                <GlassPanel sx={{ p: 1.2, textAlign: "center" }}>
                  <Typography
                    sx={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}
                  >
                    EST. PAYOUT
                  </Typography>
                  <Typography
                    sx={{ color: "#89edff", fontWeight: 1000, fontSize: 22 }}
                  >
                    {fmt(diceEstimatedMultiplier)}x
                  </Typography>
                </GlassPanel>
              </Grid>
            </Grid>

            <BusyButton
              fullWidth
              variant="contained"
              onClick={playDice}
              busy={playing}
              busyText="ROLLING…"
              sx={{
                mt: 2,
                color: "#00151d",
                background: "linear-gradient(90deg,#58d7ff,#a8f5ff)",
              }}
            >
              ROLL THE DICE
            </BusyButton>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box sx={{ minHeight: 330, display: "grid", placeItems: "center" }}>
              <Box
                sx={{
                  width: 190,
                  height: 190,
                  borderRadius: "38px",
                  display: "grid",
                  placeItems: "center",
                  background:
                    "linear-gradient(145deg,#ecfbff,#7be7ff 48%,#1769aa)",
                  border: "9px solid white",
                  boxShadow: "0 0 58px rgba(88,215,255,.62)",
                  animation: diceAnimating
                    ? `${diceRoll} .95s ease-in-out`
                    : "none",
                }}
              >
                <Typography
                  sx={{ color: "#05253a", fontWeight: 1000, fontSize: 52 }}
                >
                  {result?.result?.roll !== undefined
                    ? fmt(result.result.roll, 2)
                    : "00.00"}
                </Typography>
              </Box>
              <Typography
                sx={{
                  mt: 1.5,
                  color: "#78e4ff",
                  fontWeight: 1000,
                  fontSize: 18,
                }}
              >
                {result?.result?.roll !== undefined
                  ? `${result.result.roll} ${result.result.mode} ${result.result.target}`
                  : `${diceMode} ${diceTarget}`}
              </Typography>
            </Box>
            <ResultBanner round={result} />
          </Grid>
        </Grid>
      </CasinoDialog>

      {/* ======================================================
          BONE MINES
      ====================================================== */}
      <CasinoDialog
        open={activeGame === "boneMines"}
        onClose={closeGame}
        title="💣 Bone Mines"
        subtitle="Open safe tiles and cash out before finding a mine."
        color="#ff596f"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <BetInput
              value={bet}
              onChange={setBet}
              min={minBet}
              max={maxBet}
              disabled={playing || mineRound?.status === "OPEN"}
              balance={userBalance}
            />

            <Box sx={{ mt: 2, px: 1 }}>
              <Typography sx={{ fontWeight: 900, color: "#ff8294" }}>
                Mines: {mineCount}
              </Typography>
              <Slider
                value={mineCount}
                min={Number(selectedGame?.minMines || 1)}
                max={Number(selectedGame?.maxMines || 10)}
                step={1}
                marks
                disabled={playing || mineRound?.status === "OPEN"}
                onChange={(_, value) => setMineCount(Number(value))}
                sx={{ color: "#ff596f" }}
              />
            </Box>

            <GlassPanel
              sx={{ p: 1.4, mt: 1.5, borderColor: "rgba(255,89,111,.28)" }}
            >
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  sx={{ color: "rgba(255,255,255,.58)", fontSize: 12 }}
                >
                  Current multiplier
                </Typography>
                <Typography sx={{ color: "#ff8fa0", fontWeight: 1000 }}>
                  {fmt(mineRound?.currentMultiplier || 0)}x
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 0.5 }}
              >
                <Typography
                  sx={{ color: "rgba(255,255,255,.58)", fontSize: 12 }}
                >
                  Cashout preview
                </Typography>
                <Typography sx={{ color: "#ffd05c", fontWeight: 1000 }}>
                  {fmt(minePayoutPreview)} HUESO
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 0.5 }}
              >
                <Typography
                  sx={{ color: "rgba(255,255,255,.58)", fontSize: 12 }}
                >
                  Safe tiles opened
                </Typography>
                <Typography sx={{ fontWeight: 1000 }}>
                  {mineRound?.openedCells?.length || 0}
                </Typography>
              </Stack>
            </GlassPanel>

            {mineRound?.status === "OPEN" ? (
              <BusyButton
                fullWidth
                variant="contained"
                onClick={cashoutMines}
                busy={playing}
                busyText="CASHING OUT…"
                disabled={!mineRound?.openedCells?.length}
                sx={{
                  mt: 2,
                  color: "#00150c",
                  background: "linear-gradient(90deg,#65ffb3,#deff67)",
                }}
              >
                CASH OUT NOW
              </BusyButton>
            ) : (
              <BusyButton
                fullWidth
                variant="contained"
                onClick={startMineGame}
                busy={playing}
                busyText="STARTING…"
                sx={{
                  mt: 2,
                  color: "white",
                  background: "linear-gradient(90deg,#ff596f,#ff8f45)",
                }}
              >
                START MINES
              </BusyButton>
            )}
          </Grid>

          <Grid item xs={12} md={8}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(5, minmax(44px, 1fr))",
                gap: { xs: 0.65, sm: 1 },
                p: { xs: 0.5, sm: 1 },
                perspective: "900px",
              }}
            >
              {Array.from({ length: 25 }, (_, cell) => {
                const opened = mineRound?.openedCells?.includes(cell);
                const mineCells = mineRound?.result?.mineCells || [];
                const isMine = opened && mineCells.includes(cell);
                const isHit = mineRound?.result?.hitCell === cell;
                const canOpen =
                  mineRound?.status === "OPEN" && !opened && !playing;

                return (
                  <Tooltip
                    key={cell}
                    title={
                      canOpen
                        ? `Open tile ${cell + 1}`
                        : opened
                          ? "Opened"
                          : "Start a round first"
                    }
                    arrow
                  >
                    <span>
                      <Button
                        onClick={() => openMineCell(cell)}
                        disabled={!canOpen}
                        sx={{
                          p: 0,
                          minWidth: 0,
                          width: "100%",
                          aspectRatio: "1 / 1",
                          borderRadius: { xs: "10px", sm: "15px" },
                          fontSize: { xs: 24, sm: 34 },
                          color: "white",
                          border: opened
                            ? isMine
                              ? "2px solid #ff4c66"
                              : "2px solid #63ffc1"
                            : "1px solid rgba(255,255,255,.15)",
                          background: opened
                            ? isMine
                              ? "radial-gradient(circle,#ff8b9c,#5b0716)"
                              : "radial-gradient(circle,#9affd2,#0c6043)"
                            : "linear-gradient(145deg,#2a1736,#0b0711)",
                          boxShadow: opened
                            ? isMine
                              ? "0 0 22px rgba(255,62,91,.7)"
                              : "0 0 20px rgba(73,255,176,.45)"
                            : "inset 0 0 12px rgba(255,255,255,.035)",
                          animation:
                            lastMineCell === cell && opened
                              ? `${tileReveal} .55s ease-out`
                              : "none",
                          transition:
                            "transform .15s ease, box-shadow .15s ease",
                          "&:hover": {
                            transform: "translateY(-3px) scale(1.035)",
                          },
                        }}
                      >
                        {opened ? (isMine || isHit ? "💣" : "🦴") : "?"}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })}
            </Box>

            {!mineRound?.roundId && (
              <Typography
                sx={{
                  textAlign: "center",
                  color: "rgba(255,255,255,.48)",
                  mt: 1,
                }}
              >
                Set your bet and number of mines, then start the round.
              </Typography>
            )}

            <ResultBanner round={result} />
          </Grid>
        </Grid>
      </CasinoDialog>

      {/* ======================================================
          HARVEST WHEEL
      ====================================================== */}
      <CasinoDialog
        open={activeGame === "harvestWheel"}
        onClose={closeGame}
        title="🎡 Harvest Wheel"
        subtitle="Farm rewards, multipliers and a neon jackpot tile."
        color="#70ff9b"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <BetInput
              value={bet}
              onChange={setBet}
              min={minBet}
              max={maxBet}
              disabled={playing}
              balance={userBalance}
            />

            <Stack spacing={0.6} sx={{ mt: 2 }}>
              {(selectedGame?.segments || FALLBACK_WHEEL_SEGMENTS).map(
                (segment) => (
                  <Box
                    key={segment.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 1,
                      py: 0.55,
                      borderRadius: 1.2,
                      bgcolor: "rgba(255,255,255,.04)",
                    }}
                  >
                    <Typography sx={{ fontSize: 13 }}>
                      {segment.emoji} {segment.label}
                    </Typography>
                    <Typography sx={{ color: "#7bffc2", fontWeight: 900 }}>
                      {fmt(segment.multiplier)}x
                    </Typography>
                  </Box>
                ),
              )}
            </Stack>

            <BusyButton
              fullWidth
              variant="contained"
              onClick={playWheel}
              busy={playing}
              busyText="SPINNING…"
              sx={{
                mt: 2,
                color: "#00170b",
                background: "linear-gradient(90deg,#70ff9b,#e7ff66)",
              }}
            >
              SPIN THE WHEEL
            </BusyButton>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box
              sx={{
                minHeight: 350,
                display: "grid",
                placeItems: "center",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 6,
                  zIndex: 3,
                  width: 0,
                  height: 0,
                  borderLeft: "18px solid transparent",
                  borderRight: "18px solid transparent",
                  borderTop: "34px solid #fff",
                  filter: "drop-shadow(0 0 7px rgba(255,255,255,.8))",
                }}
              />

              <Box
                sx={{
                  width: { xs: 255, sm: 290 },
                  height: { xs: 255, sm: 290 },
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  border: "12px solid #70ff9b",
                  background:
                    "conic-gradient(#ffdd55 0 14%,#ff6c91 14% 28%,#6fdcff 28% 42%,#8d72ff 42% 56%,#72ffac 56% 70%,#ff8d35 70% 84%,#ff63dc 84% 100%)",
                  boxShadow: "0 0 48px rgba(112,255,155,.55)",
                  animation: wheelSpinning
                    ? `${spinWheel} 1.55s cubic-bezier(.2,.7,.2,1)`
                    : "none",
                }}
              >
                <Box
                  sx={{
                    width: 108,
                    height: 108,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "#0c0714",
                    border: "6px solid white",
                    fontSize: 58,
                  }}
                >
                  {result?.result?.emoji || "🦴"}
                </Box>
              </Box>

              <Typography
                sx={{ mt: 1, color: "#8affbd", fontWeight: 1000, fontSize: 21 }}
              >
                {result?.result?.label || "READY TO SPIN"}
              </Typography>
            </Box>
            <ResultBanner round={result} />
          </Grid>
        </Grid>
      </CasinoDialog>

      {/* ======================================================
          DOG RACE
      ====================================================== */}
      <CasinoDialog
        open={activeGame === "dogRace"}
        onClose={closeGame}
        title="🏁 Dog Race"
        subtitle="Different dogs have different odds and multipliers."
        color="#cf7dff"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <BetInput
              value={bet}
              onChange={setBet}
              min={minBet}
              max={maxBet}
              disabled={playing}
              balance={userBalance}
            />

            <Stack spacing={0.8} sx={{ mt: 2 }}>
              {(selectedGame?.dogs || FALLBACK_DOGS).map((dog) => (
                <Button
                  key={dog.id}
                  variant={dogId === dog.id ? "contained" : "outlined"}
                  onClick={() => setDogId(dog.id)}
                  disabled={playing}
                  sx={{
                    justifyContent: "space-between",
                    color: dogId === dog.id ? "#130018" : "white",
                    borderColor: "rgba(207,125,255,.55)",
                    bgcolor: dogId === dog.id ? "#cf7dff" : "transparent",
                    fontWeight: 900,
                  }}
                >
                  <span>
                    {dog.emoji} {dog.name}
                  </span>
                  <span>
                    {dog.chance}% · {fmt(dog.multiplier)}x
                  </span>
                </Button>
              ))}
            </Stack>

            <BusyButton
              fullWidth
              variant="contained"
              onClick={playRace}
              busy={playing}
              busyText="RACING…"
              sx={{
                mt: 2,
                color: "#16001d",
                background: "linear-gradient(90deg,#cf7dff,#ff76cf)",
              }}
            >
              START RACE
            </BusyButton>
          </Grid>

          <Grid item xs={12} md={8}>
            <Stack spacing={0.8} sx={{ py: 1 }}>
              {(selectedGame?.dogs || FALLBACK_DOGS).map((dog, index) => {
                const position = result?.result?.positions?.find(
                  (item) => item.id === dog.id,
                )?.position;
                const raceDuration = position
                  ? 0.62 + position * 0.12
                  : 0.85 + index * 0.08;

                return (
                  <Box
                    key={dog.id}
                    sx={{
                      position: "relative",
                      height: 54,
                      borderRadius: "13px",
                      overflow: "hidden",
                      border:
                        dogId === dog.id
                          ? "1px solid rgba(232,177,255,.75)"
                          : "1px solid rgba(255,255,255,.13)",
                      background:
                        index % 2
                          ? "rgba(255,255,255,.035)"
                          : "rgba(207,125,255,.075)",
                    }}
                  >
                    <Typography
                      sx={{
                        position: "absolute",
                        left: 8,
                        top: 3,
                        zIndex: 2,
                        fontSize: 12,
                        fontWeight: 900,
                      }}
                    >
                      {dog.name}
                    </Typography>
                    <Typography
                      sx={{
                        position: "absolute",
                        left: 8,
                        bottom: 2,
                        zIndex: 2,
                        color: "rgba(255,255,255,.48)",
                        fontSize: 10,
                      }}
                    >
                      Lane {index + 1}
                    </Typography>

                    <Box
                      sx={{
                        position: "absolute",
                        left: 72,
                        right: 14,
                        top: 5,
                        bottom: 5,
                        borderLeft: "2px dashed rgba(255,255,255,.18)",
                        borderRight: "5px double #fff",
                      }}
                    >
                      <Typography
                        sx={{
                          width: 54,
                          fontSize: 35,
                          lineHeight: 1,
                          animation:
                            raceRunning || position
                              ? `${raceRun} ${raceDuration}s ease-out forwards`
                              : "none",
                        }}
                      >
                        {dog.emoji}
                      </Typography>
                    </Box>

                    {position && (
                      <Chip
                        size="small"
                        label={`#${position}`}
                        sx={{
                          position: "absolute",
                          right: 18,
                          top: 13,
                          color: position === 1 ? "#1a0c00" : "white",
                          bgcolor:
                            position === 1 ? "#ffd34e" : "rgba(0,0,0,.65)",
                          fontWeight: 1000,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>

            {result?.result?.winnerName && (
              <Typography
                sx={{
                  textAlign: "center",
                  color: "#e6b8ff",
                  fontSize: 25,
                  fontWeight: 1000,
                }}
              >
                🏆 {result.result.winnerEmoji} {result.result.winnerName} WINS!
              </Typography>
            )}

            <ResultBanner round={result} />
          </Grid>
        </Grid>
      </CasinoDialog>

      {/* NOTIFICACIONES */}
      <Snackbar
        open={notice.open}
        autoHideDuration={6500}
        onClose={() => setNotice((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={notice.type}
          variant="filled"
          onClose={() => setNotice((current) => ({ ...current, open: false }))}
          sx={{ width: "100%", fontWeight: 850 }}
        >
          {notice.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CasinoPage;
